import { useEffect, useState } from "react";
import { Download } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { boxQty } from "../lib/stock";

const ROLE = { admin: "Admin", employee: "Kitchen", franchisee: "Franchisee" };
const sel = "h-11 rounded-xl border border-input bg-card px-3 text-sm";

const StockChanges = ({ list }) => (
  <div className="space-y-0.5">
    {list.map((s, i) => (
      <div key={i} className="text-xs whitespace-nowrap">
        {s.item} <span className="text-muted-foreground">@ {s.location_name}</span>: {boxQty(s.before, s.category)} → <b>{boxQty(s.after, s.category)}</b>
      </div>
    ))}
  </div>
);

const Filters = ({ f, setF, users, locations, onExport }) => (
  <div className="flex flex-wrap gap-3 items-end mb-4" data-testid="audit-filters">
    <select className={sel} value={f.user_id} onChange={(e) => setF({ ...f, user_id: e.target.value })} data-testid="audit-filter-user">
      <option value="">All people</option>
      {users.map((u) => <option key={u.id} value={u.id}>{u.name} ({ROLE[u.role] || u.role})</option>)}
    </select>
    <select className={sel} value={f.location_id} onChange={(e) => setF({ ...f, location_id: e.target.value })} data-testid="audit-filter-location">
      <option value="">All locations</option>
      {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
    </select>
    <input type="date" className={sel} value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} data-testid="audit-filter-from" />
    <input type="date" className={sel} value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} data-testid="audit-filter-to" />
    <button onClick={onExport} data-testid="audit-export-btn"
      className="h-11 px-5 rounded-xl bg-foreground text-background text-sm font-medium inline-flex items-center gap-2 hover:opacity-90 transition-opacity">
      <Download className="h-4 w-4" /> Export CSV
    </button>
  </div>
);

export const AuditLogTable = () => {
  const [f, setF] = useState({ user_id: "", location_id: "", date_from: "", date_to: "" });
  const [rows, setRows] = useState(null);
  const [users, setUsers] = useState([]);
  const [locations, setLocations] = useState([]);
  const qs = new URLSearchParams(Object.entries(f).filter(([, v]) => v)).toString();

  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data));
    api.get("/locations").then((r) => setLocations(r.data));
  }, []);
  useEffect(() => { setRows(null); api.get(`/admin/audit-logs?limit=1000&${qs}`).then((r) => setRows(r.data)); }, [qs]);

  const exportCsv = async () => {
    try {
      const r = await api.get(`/admin/audit-logs/export?${qs}`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = `audit_log_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
    } catch { toast.error("Export failed"); }
  };

  return (
    <div>
      <Filters f={f} setF={setF} users={users} locations={locations} onExport={exportCsv} />
      <div className="luxury-card overflow-x-auto">
        <table className="w-full text-sm" data-testid="audit-log-table">
          <thead className="bg-secondary/60">
            <tr className="text-left">
              {["When", "Person", "Location", "Action", "Result", "Stock Before → After"].map((h) => (
                <th key={h} className="px-5 py-3 label-eyebrow font-medium">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(rows || []).map((r) => (
              <tr key={r.id} className="border-t border-border align-top" data-testid={`audit-row-${r.id}`}>
                <td className="px-5 py-3 text-muted-foreground whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</td>
                <td className="px-5 py-3">
                  <div className="font-medium">{r.user_name || r.user_email || "Unknown"}</div>
                  <div className="text-xs text-muted-foreground">{ROLE[r.role] || r.role || ""}</div>
                </td>
                <td className="px-5 py-3">{r.role === "admin" ? "All locations" : (r.location_name || "—")}</td>
                <td className="px-5 py-3 font-medium">{r.action}</td>
                <td className="px-5 py-3">
                  {r.success
                    ? <span className="text-[hsl(var(--accent-sage))] font-medium">OK</span>
                    : <span className="text-destructive font-medium">Failed ({r.status_code})</span>}
                </td>
                <td className="px-5 py-3">{r.stock_changes?.length ? <StockChanges list={r.stock_changes} /> : "—"}</td>
              </tr>
            ))}
            {rows && rows.length === 0 && (
              <tr><td colSpan="6" className="px-5 py-16 text-center text-muted-foreground text-sm">No actions recorded for these filters.</td></tr>
            )}
            {!rows && (
              <tr><td colSpan="6" className="px-5 py-16 text-center text-muted-foreground text-sm">Loading…</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
